/**
 * @module PagosOnline
 * @description Componente raíz del módulo de pagos en línea de Comfenalco.
 * Implementa un flujo de pago tipo wizard con 3 pasos:
 *
 * 1. **Identificación** - Tipo y número de documento del usuario
 * 2. **Referencia de pago** - Selección de documentos pendientes agrupados por servicio
 * 3. **Detalle y confirmación** - Resumen del pago e integración con ePayco
 *
 * Arquitectura:
 * - PagosOnlineProvider: Contexto que centraliza estado y lógica de negocio
 * - PagosOnlineContent: Presentación del card, stepper y formulario
 * - Sub-componentes: StepperNav, StepIdentificacion, StepReferenciaPago,
 *   StepDetallePago, StepperActions
 *
 * Integraciones externas:
 * - ePayco: Pasarela de pago (checkout embebido)
 * - APIs backend: Tipos de documento, servicios de venta, documentos de pago
 *
 * Parámetros URL soportados:
 * - :idpago - Activa flujo de pago rápido (precarga datos desde API)
 * - ?ocultarLogo=true - Oculta logo de Comfenalco
 * - ?cedula=XXX&tdocumento=CC - Pre-rellena campos y auto-avanza
 * - ?showCard=false - Aplica estilo alternativo al contenedor
 */

import { FC } from "react";
import { toAbsoluteUrl } from "../../helpers";
import { Formik, Form } from "formik";

import { PagosOnlineProvider, usePagosOnline } from "./PagosOnlineContext";
import { StepperNav } from "./components/StepperNav";
import { StepIdentificacion } from "./components/StepIdentificacion";
import { StepReferenciaPago } from "./components/StepReferenciaPago";
import { StepDetallePago } from "./components/StepDetallePago";
import { StepperActions } from "./components/StepperActions";
import { IAlerta } from "./IAlerta";

import { MyLoader } from "app/modules/comfeModules/MyLoader";

/**
 * PagosOnlineContent - Componente interno que renderiza la interfaz completa
 * del flujo de pagos. Consume el contexto PagosOnline para obtener estado,
 * referencias DOM y funciones de navegación.
 *
 * Estructura del layout:
 * - MyLoader: Overlay de carga bloqueante
 *   - Card con header (logo + título "Recaudos")
 *   - Stepper visual (3 pasos con indicadores)
 *   - Imagen lateral decorativa (solo desktop)
 *   - Formik Form con los 3 pasos + acciones
 *   - IAlerta para errores de validación
 */
const PagosOnlineContent: FC = () => {
  const {
    pantallaBloqueada,
    stepperRef,
    ocultarLogo,
    showCard,
    currentSchema,
    initValues,
    submitStep,
    showError,
    errorMensaje,
  } = usePagosOnline();

  return (
    <>
      <MyLoader loading={pantallaBloqueada}>
        <div id="contenedor-principal-pagos-online">
          <div className="Container-fluid pt-md-18 ">
            <div className="">
              <div className={"col-xs-12 col-sm-12 col-md-12 col-lg-12"}>
                <div
                  className={"card-custom bg-white shadow "+
                             "col-xs-12 offset-xs-0 "+
                             "col-sm-12 offset-sm-0 "+
                             "col-md-10 offset-md-1 "+
                             "col-xl-8 offset-xl-2 " +
                             "card rounded-4 "+ ((showCard === "false") && "card2")}
                  id="contenedor-pago-virtual2"
                >
                  {/* ── Header: Logo de Comfenalco y título ── */}
                  <div className="card-header2">
                    <img
                      className={"img-fluid o-logo ps-8 " + ((ocultarLogo === "true") ? "d-none" : "")}
                      alt="Comfenalco"
                      src={toAbsoluteUrl(
                        "/media/logos/logoComfenalcoHorizontal.svg"
                      )}
                      width={"42%"}
                    ></img>
                    <h3 className="card-title fs-1 ms-5">Recaudos</h3>
                    <div className="card-toolbar"></div>
                  </div>

                  {/* ── Body: Stepper + Formulario multi-paso ── */}
                  <div className="card-body">
                    <div
                      ref={stepperRef}
                      className="stepper stepper-pills"
                      id=""
                    >
                      {/* Indicadores visuales de los 3 pasos */}
                      <StepperNav />

                      <div className="row pt-15">
                        {/* Imagen lateral decorativa (oculta en mobile) */}
                        <div className="col-md-4 d-xs-none d-sm-none d-dm-block d-lg-block d-none col-lg-3 col-xl-3">
                          <img
                            alt="pagando"
                            src={toAbsoluteUrl(
                              "/media/illustrations/pagando.png"
                            )}
                            className="img-fluid rounded-4"
                          ></img>
                        </div>

                        {/* Área principal del formulario con los pasos del stepper */}
                        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-9 col-xl-9 rounded-4 border border-light">
                          <Formik
                            validationSchema={currentSchema}
                            initialValues={initValues}
                            onSubmit={submitStep}
                          >
                            <Form
                              className="form"
                              noValidate
                              id="kt_modal_create_app_form"
                            >
                              {/* Paso 1: Tipo y número de documento */}
                              <StepIdentificacion />

                              {/* Paso 2: Selección de referencia de pago */}
                              <StepReferenciaPago />

                              {/* Paso 3: Resumen y confirmación del pago */}
                              <StepDetallePago />

                              {/* Paso 4: Contenedor vacío (requerido por StepperComponent) */}
                              <div data-kt-stepper-element="content">
                                <div className="w-100"></div>
                              </div>

                              {/* Botones de navegación: retroceder / continuar / checkout */}
                              <StepperActions />
                            </Form>
                          </Formik>
                        </div>
                      </div>
                    </div>

                    {/* Alerta de error: visible cuando hay validaciones fallidas */}
                    {showError && errorMensaje !== "" && (
                      <IAlerta
                        tipo={"bg-danger"}
                        mensaje={errorMensaje}
                      ></IAlerta>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MyLoader>
    </>
  );
};

/**
 * PagosOnline - Componente de entrada del módulo.
 * Envuelve PagosOnlineContent con el Provider de contexto para que todos
 * los sub-componentes del árbol puedan acceder al estado compartido.
 */
const PagosOnline: FC = () => {
  return (
    <PagosOnlineProvider>
      <PagosOnlineContent />
    </PagosOnlineProvider>
  );
};

export { PagosOnline };

